import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { CheckCircleIcon, XCircleIcon } from "lucide-react";
import { Spinner } from "../../icons/Spinner";
import {
  countUploadItems,
  getAllFilesFromFolders,
  hasActiveUploads,
  useUploadContext,
} from "./useUploadContext";

const TOAST_ID = "data-room-upload-progress";
const PROGRESS_TOAST_THROTTLE_MS = 250;

/**
 * Progress UI for background uploads. Custom card UI, with progress updates
 * throttled so we don't rebuild the toast tree on every multipart tick.
 */
export function UploadProgressToast() {
  const {
    uploadFiles,
    uploadFolders,
    isUploading,
    targetFolderName,
    cancelUploads,
    clearQueue,
  } = useUploadContext();

  const hadActivityRef = useRef(false);
  const lastProgressToastAtRef = useRef(0);
  const lastProgressKeyRef = useRef("");
  const cancelUploadsRef = useRef(cancelUploads);
  cancelUploadsRef.current = cancelUploads;
  const clearQueueRef = useRef(clearQueue);
  clearQueueRef.current = clearQueue;

  useEffect(() => {
    const allFiles = [
      ...uploadFiles,
      ...getAllFilesFromFolders(uploadFolders),
    ];
    const { total, completed, errored } = countUploadItems(
      uploadFiles,
      uploadFolders,
    );

    const inFlight =
      isUploading || hasActiveUploads(uploadFiles, uploadFolders);

    if (!inFlight && !hadActivityRef.current) {
      return;
    }

    if (!inFlight && completed === 0 && errored === 0) {
      if (hadActivityRef.current) {
        toast.dismiss(TOAST_ID);
        hadActivityRef.current = false;
        lastProgressKeyRef.current = "";
      }
      return;
    }

    hadActivityRef.current = true;

    const fileProgressSum = allFiles.reduce((sum, f) => {
      if (f.status === "complete") return sum + 100;
      if (f.status === "error" || f.status === "cancelled") return sum;
      return sum + (f.uploadProgress || 0);
    }, 0);
    const aggregatePct =
      allFiles.length === 0
        ? completed > 0
          ? 100
          : 0
        : Math.round(fileProgressSum / allFiles.length);

    const destination = targetFolderName ? ` to ${targetFolderName}` : "";
    const countLabel = Math.max(total, allFiles.length);

    if (inFlight) {
      const progressKey = `${completed}:${countLabel}:${aggregatePct}:${destination}`;
      const now = Date.now();
      const due =
        progressKey !== lastProgressKeyRef.current &&
        (lastProgressKeyRef.current === "" ||
          now - lastProgressToastAtRef.current >= PROGRESS_TOAST_THROTTLE_MS);

      if (!due) {
        return;
      }

      lastProgressToastAtRef.current = now;
      lastProgressKeyRef.current = progressKey;

      toast.custom(
        () => (
          <div className="w-[356px] overflow-hidden rounded-lg border border-concrete-200 bg-white shadow-lg">
            <div className="flex items-start justify-between gap-3 px-4 py-3">
              <div className="flex items-start gap-2 min-w-0">
                <span className="mt-0.5 shrink-0">
                  <Spinner color="gray" size="xs" withMargin={false} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-concrete-900">
                    Uploading {completed} of {countLabel}
                    {destination}
                  </p>
                  <p className="text-xs text-concrete-500 mt-0.5">
                    {aggregatePct}% complete
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-concrete-600 hover:bg-concrete-50"
                onClick={() => {
                  cancelUploadsRef.current();
                }}
              >
                Cancel
              </button>
            </div>
            <div className="h-1 w-full bg-concrete-100">
              <div
                className="h-full bg-persian-500 transition-all duration-300"
                style={{ width: `${aggregatePct}%` }}
              />
            </div>
          </div>
        ),
        { id: TOAST_ID, duration: Infinity },
      );
      return;
    }

    lastProgressKeyRef.current = "";

    if (errored > 0) {
      toast.custom(
        (toastId) => (
          <div className="w-[356px] overflow-hidden rounded-lg border border-concrete-200 bg-white shadow-lg">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <XCircleIcon className="w-4 h-4 shrink-0 text-red-500" />
                <p className="text-sm font-medium text-concrete-900">
                  {completed} uploaded, {errored} failed
                </p>
              </div>
              <button
                type="button"
                className="shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-concrete-600 hover:bg-concrete-50"
                onClick={() => {
                  clearQueueRef.current();
                  toast.dismiss(toastId);
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        ),
        { id: TOAST_ID, duration: Infinity },
      );
      return;
    }

    toast.custom(
      () => (
        <div className="w-[356px] overflow-hidden rounded-lg border border-concrete-200 bg-white shadow-lg">
          <div className="flex items-center gap-2 px-4 py-3">
            <CheckCircleIcon className="w-4 h-4 shrink-0 text-green-600" />
            <p className="text-sm font-medium text-concrete-900">
              Uploaded {completed} item{completed === 1 ? "" : "s"}
              {destination}
            </p>
          </div>
        </div>
      ),
      { id: TOAST_ID, duration: 4000 },
    );

    const timeout = window.setTimeout(() => {
      clearQueue();
      hadActivityRef.current = false;
    }, 4200);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [
    uploadFiles,
    uploadFolders,
    isUploading,
    targetFolderName,
    clearQueue,
  ]);

  return null;
}
